import React, { useState, useCallback, createContext, useContext } from "react";
import AlertModal, { AlertType } from "../components/AlertModal";
import ConfirmModal, { ConfirmType } from "../components/ConfirmModal";

interface AlertOptions {
  title?: string;
  type?: AlertType;
  buttonText?: string;
}

interface ConfirmOptions {
  title?: string;
  type?: ConfirmType;
  confirmText?: string;
  cancelText?: string;
}

interface ModalContextType {
  showAlert: (message: string, options?: AlertOptions) => void;
  showSuccess: (message: string, options?: AlertOptions) => void;
  showWarning: (message: string, options?: AlertOptions) => void;
  showError: (message: string, options?: AlertOptions) => void;
  showConfirm: (message: string, options?: ConfirmOptions) => Promise<boolean>;
}

const ModalContext = createContext<ModalContextType | null>(null);

interface AlertState {
  isOpen: boolean;
  message: string;
  title?: string;
  type: AlertType;
  buttonText: string;
}

interface ConfirmState {
  isOpen: boolean;
  message: string;
  title?: string;
  type: ConfirmType;
  confirmText: string;
  cancelText: string;
  resolve: ((value: boolean) => void) | null;
}

export const ModalProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [alertState, setAlertState] = useState<AlertState>({
    isOpen: false,
    message: "",
    type: "info",
    buttonText: "Tamam",
  });

  const [confirmState, setConfirmState] = useState<ConfirmState>({
    isOpen: false,
    message: "",
    type: "info",
    confirmText: "Onayla",
    cancelText: "İptal",
    resolve: null,
  });

  const showAlert = useCallback(
    (message: string, options: AlertOptions = {}) => {
      setAlertState({
        isOpen: true,
        message,
        title: options.title,
        type: options.type || "info",
        buttonText: options.buttonText || "Tamam",
      });
    },
    [],
  );

  const showSuccess = useCallback(
    (message: string, options: AlertOptions = {}) => {
      showAlert(message, { ...options, type: "success" });
    },
    [showAlert],
  );

  const showWarning = useCallback(
    (message: string, options: AlertOptions = {}) => {
      showAlert(message, { ...options, type: "warning" });
    },
    [showAlert],
  );

  const showError = useCallback(
    (message: string, options: AlertOptions = {}) => {
      showAlert(message, { ...options, type: "error" });
    },
    [showAlert],
  );

  const showConfirm = useCallback(
    (message: string, options: ConfirmOptions = {}): Promise<boolean> => {
      return new Promise((resolve) => {
        setConfirmState({
          isOpen: true,
          message,
          title: options.title,
          type: options.type || "info",
          confirmText: options.confirmText || "Onayla",
          cancelText: options.cancelText || "İptal",
          resolve,
        });
      });
    },
    [],
  );

  const handleAlertClose = useCallback(() => {
    setAlertState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const handleConfirmConfirm = useCallback(() => {
    if (confirmState.resolve) {
      confirmState.resolve(true);
    }
    setConfirmState((prev) => ({ ...prev, isOpen: false, resolve: null }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmState.resolve]);

  const handleConfirmCancel = useCallback(() => {
    if (confirmState.resolve) {
      confirmState.resolve(false);
    }
    setConfirmState((prev) => ({ ...prev, isOpen: false, resolve: null }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmState.resolve]);

  return (
    <ModalContext.Provider
      value={{
        showAlert,
        showSuccess,
        showWarning,
        showError,
        showConfirm,
      }}
    >
      {children}
      <AlertModal
        isOpen={alertState.isOpen}
        message={alertState.message}
        title={alertState.title}
        type={alertState.type}
        buttonText={alertState.buttonText}
        onClose={handleAlertClose}
      />
      <ConfirmModal
        isOpen={confirmState.isOpen}
        message={confirmState.message}
        title={confirmState.title}
        type={confirmState.type}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        onConfirm={handleConfirmConfirm}
        onCancel={handleConfirmCancel}
      />
    </ModalContext.Provider>
  );
};

export const useModal = (): ModalContextType => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error("useModal must be used within a ModalProvider");
  }
  return context;
};
